import { useMemo, useState } from "react";
import { useLocalProgress, computeAggregateStats, AggregateStats } from "../hooks/useLocalProgress";
import { PuzzleResult, Difficulty, DIFFICULTIES, DIFFICULTY_LABELS, DIFFICULTY_COLORS, DIFFICULTY_EMOJI, getDifficultyForDate } from "../types";
import { GAME_STATE_KEY } from "../lib/constants";
import { formatDuration } from "../lib/format";
import styles from "./Stats.module.css";

interface StatsProps {
  size: "7x7" | "9x9";
  onSelectPuzzle?: (date: string, size: "7x7" | "9x9") => void;
}

function starString(count: number): string {
  return "\u2605".repeat(count) + "\u2606".repeat(6 - count);
}

function AggregatePanel({ stats, playStreak, avgDifficulty }: { stats: AggregateStats; playStreak: { current: number; longest: number }; avgDifficulty: string | null }) {
  return (
    <div className={styles.aggregate}>
      <div className={styles.statBox}>
        <div className={styles.statValue}>{stats.totalGames}</div>
        <div className={styles.statLabel}>Played</div>
      </div>
      <div className={styles.statBox}>
        <div className={styles.statValue}>{stats.totalGames > 0 ? `${stats.winPercent}%` : "-"}</div>
        <div className={styles.statLabel}>Win</div>
      </div>
      <div className={styles.statBox}>
        <div className={styles.statValue}>{playStreak.current}</div>
        <div className={styles.statLabel}>Day Streak</div>
      </div>
      <div className={styles.statBox}>
        <div className={styles.statValue}>{stats.currentWinStreak}</div>
        <div className={styles.statLabel}>Win Streak</div>
      </div>
      <div className={styles.statBox}>
        <div className={styles.statValue}>{stats.longestWinStreak}</div>
        <div className={styles.statLabel}>Best Streak</div>
      </div>
      <div className={styles.statBox}>
        <div className={styles.statValue}>{stats.wins > 0 ? stats.avgStars.toFixed(1) : "-"}</div>
        <div className={styles.statLabel}>Avg Stars</div>
      </div>
      <div className={styles.statBox}>
        <div className={styles.statValue}>
          {stats.avgFocusedMs > 0 ? formatDuration(stats.avgFocusedMs) : "-"}
        </div>
        <div className={styles.statLabel}>Avg Active</div>
      </div>
      <div className={styles.statBox}>
        <div className={styles.statValue}>
          {stats.avgTimeMs > 0 ? formatDuration(stats.avgTimeMs) : "-"}
        </div>
        <div className={styles.statLabel}>Avg Wall</div>
      </div>
      <div className={styles.statBox}>
        <div className={styles.statValue}>{avgDifficulty ?? "-"}</div>
        <div className={styles.statLabel}>Avg Difficulty</div>
      </div>
    </div>
  );
}

interface StatsEntry {
  key: string;
  result: PuzzleResult;
  completedAtDate?: string;
}

type SortMode = "puzzleDate" | "completedAt";

function ResultList({ results, onSelect }: { results: StatsEntry[]; onSelect?: (date: string, size: "7x7" | "9x9") => void }) {
  const [sortMode, setSortMode] = useState<SortMode>("completedAt");

  const sorted = useMemo(() => {
    if (sortMode === "puzzleDate") return results; // already sorted by key (puzzle date) desc
    return [...results].sort((a, b) => {
      const aDate = a.completedAtDate ?? "";
      const bDate = b.completedAtDate ?? "";
      return bDate.localeCompare(aDate) || (b.result.completedAtMs ?? 0) - (a.result.completedAtMs ?? 0); // desc
    });
  }, [results, sortMode]);

  if (results.length === 0) {
    return <p className={styles.empty}>No games played yet.</p>;
  }

  return (
    <>
      <div className={styles.sortToggle}>
        <span className={styles.sortLabel}>Sort:</span>
        <button
          className={`${styles.sortButton} ${sortMode === "completedAt" ? styles.sortActive : ""}`}
          onClick={() => setSortMode("completedAt")}
        >
          Completed At
        </button>
        <button
          className={`${styles.sortButton} ${sortMode === "puzzleDate" ? styles.sortActive : ""}`}
          onClick={() => setSortMode("puzzleDate")}
        >
          Puzzle Date
        </button>
      </div>
      <div className={styles.list}>
        {sorted.map(({ key, result, completedAtDate }) => {
          const [size, date] = key.split(":");
          const diff = getDifficultyForDate(date);
          return (
            <button
              key={key}
              className={styles.row}
              onClick={() => onSelect?.(date, size as "7x7" | "9x9")}
            >
              <div className={styles.rowDate}>
                <span className={styles.dateText}>{date}</span>
                <span
                  title={DIFFICULTY_LABELS[diff]}
                  style={{ color: DIFFICULTY_COLORS[diff], fontSize: "14px" }}
                >
                  {DIFFICULTY_EMOJI[diff]}
                </span>
                {sortMode === "completedAt" && completedAtDate && (
                  <span className={styles.completedAtDate}>
                    completed {completedAtDate}
                  </span>
                )}
              </div>
              <div className={styles.rowResult}>
                {/* overLimit && !solved = synthesized entry from game state (unsolved, ran out of swaps) */}
                {result.overLimit && !result.solved ? (
                  <span className={styles.lossMuted}>
                    Over limit ({result.swapsUsed}/{result.maxSwaps})
                  </span>
                ) : result.solved ? (
                  result.overLimit ? (
                    <span className={styles.loss}>
                      Over limit ({result.swapsUsed}/{result.maxSwaps})
                    </span>
                  ) : (
                    <span className={styles.win}>
                      <span className={styles.starsDisplay}>
                        {starString(result.stars)}
                      </span>
                      <span className={styles.swapInfo}>
                        {result.swapsUsed}/{result.maxSwaps}
                      </span>
                    </span>
                  )
                ) : (
                  <span className={styles.incomplete}>In progress</span>
                )}
              </div>
              <div className={styles.rowMeta}>
                {result.attempts > 1 && (
                  <span className={styles.attempts}>x{result.attempts}</span>
                )}
                {result.solved ? (
                  result.focusedMs != null && formatDuration(result.focusedMs) !== formatDuration(result.elapsedMs) ? (
                    <span className={styles.timeStack}>
                      <span>{formatDuration(result.focusedMs)}</span>
                      <span className={styles.wallTime}>{formatDuration(result.elapsedMs)}</span>
                    </span>
                  ) : formatDuration(result.elapsedMs)
                ) : "-"}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function loadGameStates(): Record<string, { swapsUsed: number; maxSwaps?: number; completedAtDate?: string }> {
  try {
    return JSON.parse(localStorage.getItem(GAME_STATE_KEY) || "{}");
  } catch {
    return {};
  }
}

function DifficultyFilter({ value, onChange }: { value: Difficulty | null; onChange: (d: Difficulty | null) => void }) {
  return (
    <div className={styles.diffFilter}>
      <button
        className={`${styles.diffPill} ${value === null ? styles.diffPillActive : ""}`}
        onClick={() => onChange(null)}
      >
        All
      </button>
      {DIFFICULTIES.map((d) => (
        <button
          key={d}
          className={`${styles.diffPill} ${value === d ? styles.diffPillActive : ""}`}
          style={value === d ? { borderColor: DIFFICULTY_COLORS[d], color: DIFFICULTY_COLORS[d] } : undefined}
          onClick={() => onChange(d)}
        >
          {DIFFICULTY_LABELS[d]}
        </button>
      ))}
    </div>
  );
}

export function Stats({ size, onSelectPuzzle }: StatsProps) {
  const { getPlayStreak, getAllResults } = useLocalProgress();
  const gameStates = useMemo(() => loadGameStates(), []);
  const [diffFilter, setDiffFilter] = useState<Difficulty | null>(null);

  const playStreak = getPlayStreak(size);
  const progressResults = getAllResults(size);

  // Merge in unsolved over-limit games from game state, enrich all with completedAtDate
  const allResults = useMemo((): StatsEntry[] => {
    const progressKeys = new Set(progressResults.map((r) => r.key));
    const extraEntries: StatsEntry[] = [];

    for (const [key, gs] of Object.entries(gameStates)) {
      if (!key.startsWith(`${size}:`)) continue;
      if (progressKeys.has(key)) continue;
      if (gs.maxSwaps == null || gs.swapsUsed < gs.maxSwaps) continue;
      extraEntries.push({
        key,
        result: {
          solved: false,
          swapsUsed: gs.swapsUsed,
          maxSwaps: gs.maxSwaps,
          stars: 0,
          elapsedMs: 0,
          size,
          overLimit: true,
          attempts: 1,
          firstTry: false,
          completedAtDate: gs.completedAtDate,
        },
        completedAtDate: gs.completedAtDate,
      });
    }

    const enriched: StatsEntry[] = progressResults.map((r) => ({
      ...r,
      completedAtDate: r.result.completedAtDate,
    }));

    return [...enriched, ...extraEntries].sort((a, b) => b.key.localeCompare(a.key));
  }, [size, progressResults, gameStates]);

  const results = useMemo(() => {
    if (!diffFilter) return allResults;
    return allResults.filter(({ key }) => {
      const date = key.split(":")[1];
      if (!date || date === "random") return false;
      return getDifficultyForDate(date) === diffFilter;
    });
  }, [allResults, diffFilter]);

  const nonRandom = useMemo(() =>
    results
      .filter(({ key }) => !key.endsWith(":random"))
      .sort((a, b) => {
        const aDate = a.completedAtDate ?? "";
        const bDate = b.completedAtDate ?? "";
        return aDate.localeCompare(bDate) || (a.result.completedAtMs ?? 0) - (b.result.completedAtMs ?? 0);
      }),
    [results]
  );

  const displayStats = useMemo(() => computeAggregateStats(nonRandom), [nonRandom]);

  const avgDifficulty = useMemo(() => {
    if (nonRandom.length === 0) return null;
    const totalIndex = nonRandom.reduce((sum, { key }) => {
      const date = key.split(":")[1];
      return sum + DIFFICULTIES.indexOf(getDifficultyForDate(date));
    }, 0);
    const avgIndex = Math.round(totalIndex / nonRandom.length);
    return DIFFICULTY_LABELS[DIFFICULTIES[avgIndex]];
  }, [nonRandom]);

  return (
    <div className={styles.stats}>
      <h2 className={styles.title}>Stats</h2>

      <AggregatePanel stats={displayStats} playStreak={playStreak} avgDifficulty={avgDifficulty} />
      <DifficultyFilter value={diffFilter} onChange={setDiffFilter} />
      <ResultList results={results} onSelect={onSelectPuzzle} />
    </div>
  );
}
